import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Car, Camera, DollarSign, CheckCircle2 } from "lucide-react";
import { localBusinessSchema, faqSchema, breadcrumbSchema, serviceAreas } from "@/lib/localBusiness";
import { CheckAvailabilityCta } from "@/components/CheckAvailabilityCta";

const PATH = "/photo-studio-near-me-columbus-ga";
const URL = `https://bookmystudiospace.com${PATH}`;

const faqs = [
  {
    q: "Where is the studio and is there parking?",
    a: "MyStudio is at 2300 Whittlesey Rd Ste E, Columbus, GA 31909, just off Whittlesey Road near the Columbus Park Crossing area. Free onsite parking is right outside the suite with ground-level load-in for gear carts, C-stands, and rolling cases.",
  },
  {
    q: "What are your studio hours?",
    a: "Bookings run Monday through Saturday, 9:00 AM to 9:00 PM Eastern. Slots are booked by the hour through the online calendar, so you can see live availability and grab same-day time when the schedule is open.",
  },
  {
    q: "What gear is included with a studio rental?",
    a: "Every booking includes strobe and continuous lighting, modifiers (softboxes, umbrellas, beauty dish), light stands, seamless paper backdrops, the cyclorama wall in Studio B, a beauty/makeup station, changing area, and client seating. Specialty backdrop colors and extra gear are add-ons you select at checkout.",
  },
  {
    q: "How much does it cost to rent studio space in Columbus, GA?",
    a: "Studio A starts at $45/hour and Studio B (with the cyc wall) starts at $70/hour, with a one-hour minimum. Add-ons like backdrop colors or the beauty station are priced individually and shown on the Rates page before you book.",
  },
  {
    q: "What should I expect for a photo or video shoot here?",
    a: "Arrive at your booked start time, check in at the suite entrance, and the space is yours for the full hour block — setup and breakdown included. Bring your own camera body and lenses; we supply the lighting and surfaces. Video and podcast crews get sound-treated walls and quiet HVAC between takes.",
  },
  {
    q: "Can I rent the studio space for a full day or recurring shoots?",
    a: "Yes. Book consecutive hourly blocks for a full-day production, or reach out through the contact page for recurring content-day scheduling and membership pricing.",
  },
  {
    q: "Do you allow teams, clients, and stylists on set?",
    a: "Absolutely. There is room for a small crew plus a client lounge area, so art directors, MUAs, stylists, and brand reps can be on set without crowding the shooting area.",
  },
];

const included = [
  "Strobe + continuous lighting with modifiers",
  "Cyclorama wall (Studio B) and seamless paper backdrops",
  "Beauty station, mirror, and changing area",
  "Free onsite parking and ground-level load-in",
  "Client lounge seating for crew and stylists",
  "Live online calendar with hourly booking",
];

const StudioNearMeColumbus = () => (
  <div className="pt-20">
    <Helmet>
      <title>Photo Studio Near Me | Studio Space for Rent in Columbus, GA</title>
      <meta
        name="description"
        content="Looking for a photo studio near you? Rent studio space in Columbus, GA by the hour — lighting, cyc wall, backdrops, beauty station, free parking. Book online."
      />
      <link rel="canonical" href={URL} />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <meta property="og:title" content="Photo Studio Near Me | Studio Space for Rent in Columbus, GA" />
      <meta property="og:description" content="Hourly photo and video studio space for rent in Columbus, GA. Lighting, cyc wall, backdrops, and free parking included." />
      <meta property="og:url" content={URL} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Photo Studio Near Me — Columbus, GA", path: PATH },
      ]))}</script>
    </Helmet>

    <section className="py-20 border-b border-border">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <div className="inline-flex items-center gap-2 text-primary font-mono text-xs tracking-widest uppercase mb-4">
          <MapPin className="w-4 h-4" /> 2300 Whittlesey Rd Ste E · Columbus, GA 31909
        </div>
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-6">
          PHOTO STUDIO NEAR ME — COLUMBUS, GA
        </h1>
        <p className="text-muted-foreground font-body text-lg max-w-2xl mx-auto mb-8">
          Studio space for rent by the hour on Whittlesey Road. Two shooting rooms, pro lighting,
          a full cyclorama wall, seamless backdrops, and free parking at the door — book online in
          under two minutes and shoot the same day when the calendar is open.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <CheckAvailabilityCta city="columbus" campaign="columbus_near_me" placement="hero">
            Check Availability &amp; Book
          </CheckAvailabilityCta>
          <Link to="/rates">
            <Button variant="outline" className="border-border font-body px-8">View Hourly Rates</Button>
          </Link>
        </div>
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-6">
          Mon–Sat · 9AM–9PM ET · Studio A from $45/hr · Studio B from $70/hr
        </p>
      </div>
    </section>

    <section className="py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-4">
          STUDIO SPACE FOR RENT, WITHOUT THE PRODUCTION HEADACHE
        </h2>
        <p className="text-center text-muted-foreground font-body max-w-2xl mx-auto mb-10">
          Most Columbus creators are shooting in living rooms, hotel suites, or driving 100 miles to
          Atlanta. MyStudio keeps it local: a purpose-built room, lights already rigged, and an
          hourly rate that works for a single headshot session or an all-day content day.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {included.map((f) => (
            <div key={f} className="bg-panel border border-border p-5 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-body text-foreground/85">{f}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          THE ESSENTIALS AT A GLANCE
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Car, title: "PARKING", body: "Free onsite lot steps from the suite door, ground-level load-in." },
            { icon: Clock, title: "HOURS", body: "Mon–Sat, 9AM–9PM ET. Hourly blocks with live online availability." },
            { icon: Camera, title: "GEAR", body: "Lighting, modifiers, stands, backdrops, cyc wall, beauty station." },
            { icon: DollarSign, title: "PRICING", body: "From $45/hr (Studio A) and $70/hr (Studio B). One-hour minimum." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-panel border border-border p-6 text-center">
              <Icon className="w-6 h-6 text-primary mx-auto mb-3" />
              <h3 className="font-display tracking-widest text-sm mb-2">{title}</h3>
              <p className="text-xs text-muted-foreground font-body">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          STUDIO RENTAL FAQ
        </h2>
        <div className="space-y-4">
          {faqs.map((f) => (
            <div key={f.q} className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-primary text-base mb-2">{f.q}</h3>
              <p className="text-sm font-body text-foreground/80">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <h2 className="text-2xl font-display tracking-widest mb-4">WHO WE SERVE</h2>
        <p className="text-sm font-body text-muted-foreground mb-8">
          {serviceAreas.join(" · ")}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <CheckAvailabilityCta
            city="columbus"
            campaign="columbus_near_me"
            placement="footer"
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-10"
          >
            Book Studio Time
          </CheckAvailabilityCta>
          <Link to="/atlanta-studio-space-rental">
            <Button variant="outline" className="border-border font-body px-8">Coming from Atlanta?</Button>
          </Link>
        </div>
        <p className="text-xs font-mono tracking-widest text-muted-foreground mt-8">
          More detail: <Link to="/columbus-ga-photo-studio-rental" className="text-primary hover:underline">Columbus studio rental overview</Link>
          {" · "}
          <Link to="/studios" className="text-primary hover:underline">Tour the studios</Link>
          {" · "}
          <Link to="/contact" className="text-primary hover:underline">Contact us</Link>
        </p>
      </div>
    </section>
  </div>
);

export default StudioNearMeColumbus;
